import { InteractionRequiredAuthError, PublicClientApplication } from '@azure/msal-browser';
import axios from 'axios';
import { API_BASE_URL } from '../constants/config';
import { loginRequest } from '../auth/msalConfig';
import { captureApiCall } from '../utils/errorCapture';
import { IMPERSONATE_ROLE_HEADER, IMPERSONATE_ROLE_STORAGE_KEY } from '../constants/roles';

/** "View as role" preview header — the backend downgrades a superadmin's role
 *  to this value per request (ignored for everyone else). See RBAC.md. */
const impersonateRoleHeader = (): Record<string, string> => {
  const role = sessionStorage.getItem(IMPERSONATE_ROLE_STORAGE_KEY);
  return role ? { [IMPERSONATE_ROLE_HEADER]: role } : {};
};

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

// Shared MSAL instance — set by AuthProvider on mount
let msalInstance: PublicClientApplication | null = null;

export const setMsalInstance = (instance: PublicClientApplication): void => {
  msalInstance = instance;
};

// Request interceptor — attach Bearer token if MSAL has an active account
apiClient.interceptors.request.use(
  async (config) => {
    // Stamp start time for duration tracking
    (config as any).__startTime = Date.now();
    Object.assign(config.headers, impersonateRoleHeader());
    if (!msalInstance) return config; // No MSAL yet (dev mode or pre-init)
    try {
      const accounts = msalInstance.getAllAccounts();
      if (accounts.length > 0) {
        const response = await msalInstance.acquireTokenSilent({
          ...loginRequest,
          account: accounts[0],
        });
        config.headers.Authorization = `Bearer ${response.idToken}`;
      }
    } catch (err) {
      if (err instanceof InteractionRequiredAuthError) {
        console.warn('Token expired — triggering interactive login');
        await msalInstance.acquireTokenRedirect(loginRequest);
        return config; // won't reach here, page redirects
      }
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// Global service status — consumed by AppShell to show banners
type ServiceStatusListener = (status: { unavailable: boolean; message: string }) => void;
let serviceStatusListener: ServiceStatusListener | null = null;
export const onServiceStatus = (listener: ServiceStatusListener | null): void => {
  serviceStatusListener = listener;
};

// Transient 503 codes the backend tags as "please try again shortly".
// Mirrors classifyDatabaseError() in backend/src/middleware/errorHandler.ts.
// POST is intentionally excluded from retry below to avoid duplicate writes.
const TRANSIENT_DB_CODES = new Set([
  'DB_CONN_CLOSED',   // pool dropped — Azure SQL closed an idle connection
  'DB_CONN_TIMEOUT',  // ECONNRESET / ETIMEDOUT — couldn't reach the DB
  'DB_TRANSIENT',     // failover / transport-level error
  'DB_NOT_FOUND',     // ENOTFOUND / server starting up
]);
const RETRYABLE_METHODS = new Set(['get', 'put', 'patch', 'delete']);

// Response interceptor — retry on 500 (Azure cold-start), log 401, detect 503
apiClient.interceptors.response.use(
  (response) => {
    // Capture successful API call for feedback metadata
    const start = (response.config as any).__startTime;
    if (start) {
      captureApiCall(response.config.url || 'unknown', response.config.method || 'unknown', response.status, Date.now() - start);
    }
    // Clear any previous service unavailable status on success
    if (serviceStatusListener) serviceStatusListener({ unavailable: false, message: '' });
    return response;
  },
  async (error) => {
    const config = error.config;

    // Capture failed API call for feedback metadata
    if (config) {
      const start = (config as any).__startTime;
      const status = error.response?.status || 0;
      captureApiCall(config.url || 'unknown', config.method || 'unknown', status, start ? Date.now() - start : 0);
    }

    // On 503 — for transient DB errors on idempotent verbs, retry once silently
    // before falling back to the service-unavailable banner. The backend resets
    // its connection pool on these errors, so a 1.5s wait is usually enough.
    if (error.response?.status === 503) {
      const code = error.response.data?.code as string | undefined;
      const method = (config?.method || '').toLowerCase();
      const canRetry = code && TRANSIENT_DB_CODES.has(code)
        && RETRYABLE_METHODS.has(method)
        && config && !config._503Retried;
      if (canRetry) {
        config._503Retried = true;
        await new Promise((r) => setTimeout(r, 1500));
        try {
          return await apiClient(config);
        } catch (retryErr) {
          // Fall through to surface the (final) 503 below
          error = retryErr as typeof error;
          if (error.response?.status !== 503) return Promise.reject(error);
        }
      }
      const msg = error.response.data?.error || 'Service temporarily unavailable. Please try again later.';
      console.warn('API returned 503:', msg);
      // Don't trigger service banner for PDF/report generation failures
      const url = config?.url || '';
      const isFeatureError = url.includes('/report') || url.includes('/export');
      if (serviceStatusListener && !isFeatureError) {
        serviceStatusListener({ unavailable: true, message: msg });
      }
      return Promise.reject(error);
    }

    // On 401, try to silently refresh the token and retry once
    if (error.response?.status === 401 && msalInstance && !config._authRetried) {
      config._authRetried = true;
      try {
        const accounts = msalInstance.getAllAccounts();
        if (accounts.length > 0) {
          const response = await msalInstance.acquireTokenSilent({
            ...loginRequest,
            account: accounts[0],
            forceRefresh: true,
          });
          config.headers.Authorization = `Bearer ${response.idToken}`;
          return apiClient(config);
        }
      } catch (refreshErr) {
        // Token can't be refreshed silently — redirect to login
        if (refreshErr instanceof InteractionRequiredAuthError) {
          console.warn('Token expired — redirecting to login');
          await msalInstance.acquireTokenRedirect(loginRequest);
          return new Promise(() => {}); // never resolves, page is redirecting
        }
      }
      return Promise.reject(error);
    }

    // Auto-retry on 500 (handles Azure cold-start)
    if (error.response?.status === 500 && config.method === 'get' &&
      (!config._retryCount || config._retryCount < 2)) {
      config._retryCount = (config._retryCount || 0) + 1;
      await new Promise((r) => setTimeout(r, 3000));
      return apiClient(config);
    }

    return Promise.reject(error);
  },
);

/** Get auth headers for non-axios requests (e.g. fetch/SSE). */
export async function getAuthHeaders(): Promise<Record<string, string>> {
  if (!msalInstance) return { ...impersonateRoleHeader() };
  try {
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length > 0) {
      const response = await msalInstance.acquireTokenSilent({ ...loginRequest, account: accounts[0] });
      return { Authorization: `Bearer ${response.idToken}`, ...impersonateRoleHeader() };
    }
  } catch { /* fall through */ }
  return { ...impersonateRoleHeader() };
}

export default apiClient;
